import axios, { AxiosResponse } from 'axios'
import { createAsyncThunk } from '@reduxjs/toolkit'

import { DhlListResponse, DhlType, DhlTypeResponse, PaymentStateType, StateType } from 'Types/admin/admin'
import { ADMIN_API_URL } from 'Constants/Global.admin.constants.ts'

import { getAccessToken, getAxiosConfig, getAxiosMultipartFormConfig } from 'Utils/auth.util.ts'
import { handleError } from 'Helpers/errors.helper.ts'

type RequestOption = {
  pageIndex: number
  pageSize: number
  param: string
  sort: 'PAID' | 'CREATED_AT' | 'NOT_PAID'
}

/**
 * Get DHL list
 */
export const getDhlList = createAsyncThunk(
  'admin/getDhl',
  async ({ pageIndex, pageSize, param, sort }: RequestOption) => {
    let url = ADMIN_API_URL.DHL.ROOT

    const queryParams = []

    if (param || param === '') {
      queryParams.push(`param=${encodeURIComponent(param)}`)
    }

    if (sort) {
      queryParams.push(`sort=${encodeURIComponent(sort)}`)
    }

    if (queryParams.length > 0) {
      const queryString = queryParams.join('&')
      url = `${url}?${queryString}&page=${pageIndex}&limit=${pageSize}`
    }

    try {
      const { data }: AxiosResponse<DhlListResponse> = await axios.get(url, getAxiosConfig())
      return {
        dhlList: data.data,
        total: data.total,
      }
    } catch (error) {
      handleError(error, true)
      throw error
    }
  }
)

/**
 * Update DHL state
 */
export const updateDhlState = createAsyncThunk(
  'admin/updateDhlState',
  async ({ id, options }: { id: string; options: { state: StateType } }) => {
    const token = getAccessToken()
    const url = `${ADMIN_API_URL.DHL.ROOT}/${id}/state`
    const config = {
      headers: {
        Authorization: token,
      },
    }

    try {
      const { data }: AxiosResponse<DhlTypeResponse> = await axios.patch(url, options, config)
      return {
        dhl: data.data,
      }
    } catch (error) {
      handleError(error, true)
      throw error
    }
  }
)

/**
 * Update DHL payment state
 */
export const updateDhlPaymentState = createAsyncThunk(
  'admin/updateDhlPaymentState',
  async ({ id, options }: { id: string; options: { paymentState: PaymentStateType } }) => {
    const token = getAccessToken()
    const url = `${ADMIN_API_URL.DHL.ROOT}/${id}/payment-state`
    const config = {
      headers: {
        Authorization: token,
      },
    }

    try {
      const { data }: AxiosResponse<DhlTypeResponse> = await axios.patch(url, options, config)
      return {
        dhl: data.data,
      }
    } catch (error) {
      handleError(error, true)
      throw error
    }
  }
)

export const orderAdminDhl = createAsyncThunk(
  'admin/orderAdminDhl',
  async (options: { externalUserId?: string; userId?: string }) => {
    const token = getAccessToken()
    const url = ADMIN_API_URL.DHL.ROOT
    const config = {
      headers: {
        Authorization: token,
      },
    }

    try {
      const { data }: AxiosResponse<DhlTypeResponse> = await axios.post(url, options, config)
      return {
        dhl: data.data,
      }
    } catch (error) {
      handleError(error, true)
      throw error
    }
  }
)

export const removeDhl = createAsyncThunk('admin/removeDhl', async (dhlId: string) => {
  const url = `${ADMIN_API_URL.DHL.ROOT}/${dhlId}`

  try {
    const { data }: AxiosResponse<{ data: DhlType }> = await axios.delete(url, getAxiosConfig())
    return {
      dhl: data.data,
    }
  } catch (error) {
    handleError(error, true)
    throw error
  }
})

export const uploadDhlPaymentSlip = async ({ dhlId, options }: { dhlId: string; options: FormData }) => {
  const url = `${ADMIN_API_URL.DHL.ROOT}/${dhlId}/payment-slip`

  try {
    const { data }: AxiosResponse<{ data: DhlType }> = await axios.put(url, options, getAxiosMultipartFormConfig())

    return {
      dhl: data.data,
    }
  } catch (error) {
    handleError(error, true)
    throw error
  }
}

export const dispatchUploadDhlPaymentSlip = createAsyncThunk('admin/dispatchUploadDhlPaymentSlip', uploadDhlPaymentSlip)
